import {
  IReferenceCode,
  REFERENCE_CODE_SEARCH_FIELDS,
  ReferenceCodeField,
  ReferenceList,
} from "@/lib/interfaces and Enums/ENUMReferenceList";
import {
  DEFAULT_SEARCH_LIMIT,
  ISearchResult,
  findItemByFieldValue,
  searchItemsByFields,
} from "@/lib/referenceDataSearchHelper";
import referenceListsStore, {
  returnReferenceListEntry,
} from "./referenceListsStore";
import { loadReferenceDataOnce } from "./referenceDataLoadHelper";

// LQC-2925 / LQC-2927 - GET /ReferenceData/{list}. One request per list, fired together.

export function returnReferenceListEndpoint(list: ReferenceList): string {
  return `/ReferenceData/${list}`;
}

// ----- Pure transform -------------------------------------------------------

// string[] or { code, name }[]. null for anything else so a contract change is an error.
// Blank codes are dropped: Frequencies includes "" (the validator allows an empty field).
export function extractReferenceCodes(response: any): IReferenceCode[] | null {
  if (!Array.isArray(response)) return null;
  const codes: IReferenceCode[] = [];
  for (const item of response) {
    if (typeof item === "string") {
      if (item.trim()) codes.push({ code: item, name: item });
    } else if (typeof item?.code === "string") {
      if (item.code.trim()) codes.push({ code: item.code, name: item.name ?? item.code });
    } else {
      return null;
    }
  }
  return codes;
}

// ----- Feed -----------------------------------------------------------------

export async function getReferenceList(
  list: ReferenceList,
  getAccessTokenSilently: Function,
  forceReload = false,
): Promise<void> {
  const store = referenceListsStore.getState();
  await loadReferenceDataOnce<IReferenceCode>({
    url: returnReferenceListEndpoint(list),
    source: `referenceListsStoreACTIONS.getReferenceList.${list}`,
    getAccessTokenSilently,
    readStatus: () =>
      returnReferenceListEntry(referenceListsStore.getState().lists, list).status,
    setStatus: (status) => store.setReferenceListStatus(list, status),
    setItems: (items) => store.setReferenceListItems(list, items),
    setError: (message) => store.setReferenceListError(list, message),
    extractItems: extractReferenceCodes,
    forceReload,
  });
}

// Parallel, and each list settles on its own: one failure never holds up the rest.
export async function getReferenceLists(
  lists: ReferenceList[],
  getAccessTokenSilently: Function,
  forceReload = false,
): Promise<void> {
  await Promise.all(
    lists.map((list) =>
      getReferenceList(list, getAccessTokenSilently, forceReload),
    ),
  );
}

// ----- Search / lookup ------------------------------------------------------

export function searchReferenceCodes(
  items: IReferenceCode[],
  term: string,
  limit: number = DEFAULT_SEARCH_LIMIT,
): ISearchResult<IReferenceCode> {
  return searchItemsByFields<IReferenceCode>({
    items,
    term,
    fields: REFERENCE_CODE_SEARCH_FIELDS,
    limit,
  });
}

export function findReferenceCode(
  items: IReferenceCode[],
  code: string,
): IReferenceCode | undefined {
  return findItemByFieldValue<IReferenceCode>(
    items,
    ReferenceCodeField.code,
    code,
  );
}
